// @flow

import * as React from 'react';

import clsx from 'clsx';
import capitalize from 'lodash/capitalize';

import { useLocation } from 'react-router-dom';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { selectCartIsWorking } from '@gandi/cart';
import type { ApplicationSummary } from '@gandi/redux-resource-simplehosting';
import { mapTags, useTranslator } from '@gandi/translate';
import {
  Base,
  Button,
  Text,
  UnorderedList,
  TypoListItem as ListItem,
  PopoverIcon,
} from '@gandi/ui';

import { formatPriceWithDiscount } from 'src/helpers';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectWebURL } from 'src/modules/config';
import type { SimplehostingPriceSummary } from 'src/modules/simplehosting/prices';

import { ProductContext } from '../ProductContext';
import { selectCartHasHostingProduct } from '../modules/selectors';

import styles from './Card.scss';

type Props = {
  application?: ApplicationSummary,
  currency: string,
  highlight: boolean,
  onClick: (evt: SyntheticMouseEvent<>) => mixed,
  packId: string,
  priceInfo: SimplehostingPriceSummary,
  withEssentialOffer: boolean,
};

// Hard code until it is included to `@gandi/ui`
function Sparkle() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="url(#paint0_linear_2437_4855)"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5.32934 0.224888L6.40719 3.16342C6.48204 3.37331 6.64671 3.52324 6.84132 3.5982L9.77545 4.67766C10.0749 4.78261 10.0749 5.21739 9.77545 5.32234L6.84132 6.4018C6.63174 6.47676 6.48204 6.64168 6.40719 6.83658L5.32934 9.77511C5.22455 10.075 4.79042 10.075 4.68563 9.77511L3.60778 6.83658C3.53293 6.62669 3.36826 6.47676 3.17365 6.4018L0.224551 5.33733C-0.0748503 5.23238 -0.0748503 4.7976 0.224551 4.69265L3.15868 3.61319C3.36826 3.53823 3.51796 3.37331 3.59281 3.17841L4.68563 0.224888C4.79042 -0.0749625 5.22455 -0.0749625 5.32934 0.224888Z" />
      <defs>
        <linearGradient
          id="paint0_linear_2437_4855"
          x1="0"
          y1="5"
          x2="10"
          y2="5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#5FE8E8" />
          <stop offset="1" stopColor="#1DDD9D" />
        </linearGradient>
      </defs>
    </svg>
  );
}

const useWebsiteCount = (
  size: SimplehostingPriceSummary['product']['name'],
): void | React.MixedElement => {
  const { __ } = useTranslator();

  if (size === 'essential' || size === 'starter')
    return mapTags(__('[[one]] website'), () => ({
      one: <strong>1</strong>,
    }));
  if (size === 'advanced')
    return mapTags(__('[[count]] websites max recommended'), () => ({
      count: <strong>5</strong>,
    }));
  if (size === 'pro')
    return mapTags(__('[[count]] websites max recommended'), () => ({
      count: <strong>10</strong>,
    }));
  if (size === 'business')
    return mapTags(__('[[count]] websites or more'), () => ({
      count: <strong>15</strong>,
    }));

  return undefined;
};

function FeaturesList({ priceInfo }: { priceInfo: SimplehostingPriceSummary }) {
  const { __ } = useTranslator();
  const webURL = useAppSelector(selectWebURL);

  const websiteCount = useWebsiteCount(priceInfo.product.name);
  const isEssential = priceInfo.product.name === 'essential';

  const GB = __('<for-translators-use>gigabytes</for-translators-use>GB');
  const MB = __('<for-translators-use>megabytes</for-translators-use>MB');

  const { cpu, ram } = priceInfo.product.params;
  const cpuText = mapTags(
    __('[[count:%(count)s]] CPU', { count: cpu }),
    () => ({
      count: <strong>{cpu}</strong>,
    }),
  );
  const ramText = mapTags(__('[[ram]] [[unit]] RAM'), () => ({
    ram: <strong>{ram < 1024 ? ram : ram / 1024}</strong>,
    unit: <strong>{ram < 1024 ? MB : GB}</strong>,
  }));

  const storage = mapTags(__('[[value]] [[unit]] storage'), () => ({
    value: <strong>{priceInfo.product.params.storage}</strong>,
    unit: <strong>{GB}</strong>,
  }));

  const mailboxesNB = isEssential ? '1' : '2';

  return (
    <UnorderedList flex flexCol icon="checkmark" iconTheme="neutral-weak">
      <ListItem flexAlignItemsCenter>
        {isEssential ? (
          mapTags(__('🇫🇷 [[one]] [[tld]] domain name'), () => ({
            one: <strong>1</strong>,
            tld: <strong>.fr</strong>,
          }))
        ) : (
          <>
            {mapTags(__('[[one]] domain name'), () => ({
              one: <strong>1</strong>,
            }))}

            <PopoverIcon
              interactive
              icon="infoWire"
              id={`packhosting${priceInfo.product.name}`}
            >
              {mapTags(
                __('Select from a [[link:list of over 80 TLDs]].'),
                ({ link: linkText }) => ({
                  link: (
                    <a
                      href={`${webURL}/hosting/free-domain-names`}
                      target="_blank"
                      rel="noopener"
                    >
                      {linkText}
                    </a>
                  ),
                }),
              )}
            </PopoverIcon>
          </>
        )}
      </ListItem>
      <ListItem>
        <Text>{storage}</Text>
      </ListItem>
      <ListItem>
        <Text>
          {cpuText}/{ramText}
        </Text>
      </ListItem>
      <ListItem>
        {mapTags(
          __('[[count:%(count)s]] mailboxes', {
            // To manage plurial/singular
            count: mailboxesNB,
          }),
          () => ({
            count: <strong>{mailboxesNB}</strong>,
          }),
        )}
      </ListItem>
      {!!websiteCount && <ListItem>{websiteCount}</ListItem>}
      <ListItem>{__('SSL/TLS Security')}</ListItem>
      <ListItem>{__('App Installation')}</ListItem>
      <ListItem>{__('Configuration choice')}</ListItem>
      <ListItem>{__('Gandi Customer Care')}</ListItem>
    </UnorderedList>
  );
}

function ExtraList({ priceInfo }: { priceInfo: SimplehostingPriceSummary }) {
  const { __ } = useTranslator();

  const size = priceInfo.product.name;

  return ['essential', 'starter', 'advanced'].includes(size) ? null : (
    <>
      <Base className={styles.divider} flex flexAlignItemsCenter>
        <Sparkle />
      </Base>
      <UnorderedList flex flexCol icon="plus" iconTheme="neutral-weak">
        <ListItem>{__('Booster')}</ListItem>
        {size === 'business' && <ListItem>{__('Extendable storage')}</ListItem>}
      </UnorderedList>
    </>
  );
}

const useTagline = (size: string, applicationName?: string) => {
  const { __ } = useTranslator();

  switch (size) {
    case 'essential':
      return __('Gandi’s affordable pack for your online presence.');
    case 'advanced':
      if (applicationName === 'nextcloud') {
        return __('Store your photos and files while assuring your privacy.');
      }
      if (applicationName === 'prestashop') {
        return __('To start your own e-boutique.');
      }
      return __('Multi-site hosting and services for your projects.');
    case 'pro':
      if (applicationName === 'nextcloud') {
        return __(
          'Synchronize files across devices and share them with your team.',
        );
      }
      if (applicationName === 'prestashop') {
        return __("For your small business's online store.");
      }
      return __('The next level to bring your professional projects to life.');
    case 'business':
      if (applicationName === 'nextcloud') {
        return __(
          'Advanced features and extensions to work efficiently with your team.',
        );
      }
      if (applicationName === 'prestashop') {
        return __(
          'For a website with a lot of traffic, optimized for additional modules.',
        );
      }
      return __('The perfect solution for your company.');
    default:
      return __('The perfect solution for blogs and portfolios.');
  }
};

const usePrice = (
  priceSummary: SimplehostingPriceSummary,
  withDiscount: boolean,
): number => {
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);

  return formatPriceWithDiscount(priceSummary, withDiscount, displayTaxes);
};

export function Card({
  application,
  currency,
  highlight,
  onClick,
  packId,
  priceInfo,
  withEssentialOffer,
}: Props): React.Node {
  const { __, formatPrice } = useTranslator();
  const location = useLocation();

  const isEssentialOffer = priceInfo.product.name === 'essential';

  const { productId } = React.useContext(ProductContext);

  const ref = React.useRef(null);

  const price = usePrice(priceInfo, false);
  const hasTaxes = priceInfo.taxes.length
    ? priceInfo.taxes[0].rate !== 0
    : false;
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);

  const isCartBusy = useAppSelector(selectCartIsWorking);
  const product = useAppSelector(state =>
    selectCartHasHostingProduct(state, {
      productId,
      size: priceInfo.product.name,
      application: application?.name,
      packId,
      min_duration: 12,
    }),
  );

  const sizeLabel = isEssentialOffer
    ? __('Essential')
    : capitalize(priceInfo.product.name);
  const priceWithDiscount = usePrice(priceInfo, !isEssentialOffer);
  const tagline = useTagline(priceInfo.product.name, application?.name);

  React.useEffect(() => {
    if (!location?.hash && highlight && ref && ref.current !== null) {
      ref.current.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'center',
      });
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Base
      backgroundColor="surface"
      className={clsx(
        styles.root,
        { [styles.highlight]: highlight },
        withEssentialOffer ? styles.essential : undefined,
      )}
      gap="small"
      grid
      innerRef={ref}
      tagName="article"
    >
      {highlight && (
        <Base
          color="white"
          className={styles.tag}
          flex
          flexAlignItemsCenter
          flexJustifyCenter
          gap="xsmall"
          size="small"
          transform="uppercase"
          weight="semibold"
        >
          <Sparkle />
          {isEssentialOffer ? 'No Bullshit' : __('Most popular')}
          <Sparkle />
        </Base>
      )}
      <Base flex flexCol>
        <Base
          className={styles.size}
          fontFamily="display"
          margin="none"
          tagName="h3"
        >
          {sizeLabel}
        </Base>
        <strong>{tagline}</strong>
      </Base>
      <Base className={styles['price-container']} flex flexCol>
        <span>
          <Text className={styles.price} weight="semibold">
            {formatPrice(priceWithDiscount, currency, { round: true })}
          </Text>{' '}
          /
          {__(
            '<for-translators-use>Periodicity (month)</for-translators-use>mo',
          )}
          {hasTaxes && <> {displayTaxes ? __('incl. VAT') : __('excl. VAT')}</>}
          <br />
          <Text color="light" size="small">
            {__('* With a 12 months plan')}
          </Text>
        </span>
        {!product ? (
          <Button
            className={styles.button}
            block
            disabled={isCartBusy}
            onClick={onClick}
          >
            {__('Choose')}
          </Button>
        ) : (
          <Button
            className={clsx(styles.button, styles.success)}
            block
            intent="success"
            iconBefore="checkmark"
            style={{ pointEvents: 'none' }}
          >
            {__(
              '<for-translators-use>No "shopping"</for-translators-use>Added to cart',
            )}
          </Button>
        )}

        <Text color="light" size="small">
          {isEssentialOffer
            ? __('Renew at the same price.')
            : __('Then %(price)s/month.', {
                price: formatPrice(price, currency),
              })}
        </Text>
      </Base>
      <Base className={styles.features} margin="none" tagName="figure">
        <Base size="small" tagName="figcaption" weight="semibold">
          {__('Included:')}
        </Base>
        <FeaturesList priceInfo={priceInfo} />
        <ExtraList priceInfo={priceInfo} />
      </Base>
    </Base>
  );
}
